import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

import Select from '../../../../Select';
import MultiSelect from '../../../../MultiSelect';
import {
  FONT_FAMILY,
  FONT_STYLE,
  TEXT_ALIGN,
  TEXT_TRANSFORM,
  googleFontInfo,
  getCardElementTextFontFamily,
} from './helpers';
import { capitalize } from '../../../../../../util/text';
import CardDesignerCardElementColorControl from '../CardDesignerCardElementControls/components/CardDesignerCardElementColorControl';
import Button from '../../../../Button';
import BoldIcon from '../../../../../icons/BoldIcon';
import ItalicIcon from '../../../../../icons/ItalicIcon';
import UppercaseIcon from '../../../../../icons/UppercaseIcon';
import FontPreload from './components/FontPreload';

const propTypes = {
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }).isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
  setLastUsedFont: PropTypes.func.isRequired,
};

const CardDesignerCardElementTextControls = ({
  element,
  onElementConfigurationChange,
  setLastUsedFont,
}) => {
  const { cardElementConfiguration = {} } = element;
  const fontFamily = getCardElementTextFontFamily(cardElementConfiguration);

  return (
    <Fragment>
      <MultiSelect
        id="font-family"
        className="card-designer-font-family-selector"
        options={Object.values(FONT_FAMILY).map(font => {
          const fontInfo = googleFontInfo.find(f => f.font === font);
          return {
            value: font,
            label: font
              .replace('GOOGLE_', '')
              .split('_')
              .map(s => capitalize(s))
              .join(' '),
            fontFamily: fontInfo?.fontFamily,
          };
        })}
        value={(() => {
          const font = cardElementConfiguration.font_family;
          if (!font) return null;
          const fontInfo = googleFontInfo.find(f => f.font === font);
          return {
            value: font,
            label: font
              .replace('GOOGLE_', '')
              .split('_')
              .map(s => capitalize(s))
              .join(' '),
            fontFamily: fontInfo?.fontFamily,
          };
        })()}
        onChange={option => {
          const value = option ? option.value : null;
          onElementConfigurationChange({
            element,
            elementConfig: {
              font_family: value,
            },
          });
          setLastUsedFont(value);
        }}
        formatOptionLabel={option => (
          <span style={{ fontFamily: option.fontFamily }}>{option.label}</span>
        )}
        styles={{
          fontFamily,
        }}
      />

      <CardDesignerCardElementColorControl
        className="ml-2"
        onChange={hex => {
          onElementConfigurationChange({
            element,
            elementConfig: {
              font_color: hex,
            },
          });
        }}
        defaultColor={cardElementConfiguration.font_color ?? undefined}
      />

      <Button
        btnStyle="white"
        className={classNames({
          'ml-2 card-designer-card-element-control-btn': true,
          'is-active': cardElementConfiguration.font_weight === 'bold',
        })}
        onClick={() => {
          onElementConfigurationChange({
            element,
            elementConfig: {
              font_weight:
                cardElementConfiguration.font_weight === 'bold' ? null : 'bold',
            },
          });
        }}
      >
        <BoldIcon />
      </Button>

      <Button
        btnStyle="white"
        className={classNames({
          'ml-2 card-designer-card-element-control-btn': true,
          'is-active':
            cardElementConfiguration.font_style === FONT_STYLE.ITALIC,
        })}
        onClick={() => {
          onElementConfigurationChange({
            element,
            elementConfig: {
              font_style:
                cardElementConfiguration.font_style === FONT_STYLE.ITALIC
                  ? FONT_STYLE.NORMAL
                  : FONT_STYLE.ITALIC,
            },
          });
        }}
      >
        <ItalicIcon />
      </Button>

      <Button
        btnStyle="white"
        className={classNames({
          'ml-2 card-designer-card-element-control-btn': true,
          'is-active':
            cardElementConfiguration.text_transform ===
            TEXT_TRANSFORM.UPPERCASE,
        })}
        onClick={() => {
          onElementConfigurationChange({
            element,
            elementConfig: {
              text_transform:
                cardElementConfiguration.text_transform ===
                TEXT_TRANSFORM.UPPERCASE
                  ? TEXT_TRANSFORM.NONE
                  : TEXT_TRANSFORM.UPPERCASE,
            },
          });
        }}
        data-for="uppercase-button"
        data-place="bottom"
        data-effect="solid"
        data-tip={
          cardElementConfiguration.text_transform === TEXT_TRANSFORM.UPPERCASE
            ? 'Lowercase'
            : 'Uppercase'
        }
      >
        <UppercaseIcon />
        <ReactTooltip id="uppercase-button" className="tooltip" />
      </Button>

      <Select
        id="text-align"
        size="sm"
        className="ml-2"
        value={cardElementConfiguration.text_align || TEXT_ALIGN.LEFT}
        onChange={event => {
          const { value } = event.target;
          onElementConfigurationChange({
            element,
            elementConfig: {
              text_align: value,
            },
          });
        }}
      >
        {Object.values(TEXT_ALIGN).map(textAlign => {
          return (
            <option key={textAlign} value={textAlign}>
              {capitalize(textAlign)}
            </option>
          );
        })}
      </Select>
      <FontPreload />
    </Fragment>
  );
};

CardDesignerCardElementTextControls.propTypes = propTypes;

export default CardDesignerCardElementTextControls;
